import { type FC, useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Calendar, Clock, MapPin, Users, GraduationCap, DollarSign, Activity as ActivityIcon, ArrowLeft, X } from 'lucide-react';
import { Button, Card, CardHeader, CardTitle, CardContent, Skeleton } from '@/components/ui';
import { supabase } from '@/lib/api/supabase';
import type { Activity } from '@/types';
import { dbToActivity } from '@/types';
import { useAuth } from '@/hooks';
import { REGISTRATIONS_CLOSED } from '@/lib/registrationsClosed';
import { ClosedNotice } from '@/components/shared';

/**
 * Activity detail page with hero gallery and comprehensive information
 */
export const ActivityDetailPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activity, setActivity] = useState<Activity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);

  useEffect(() => {
    const fetchActivity = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase
          .from('activities')
          .select('*')
          .eq('id', id)
          .single();

        if (fetchError) throw fetchError;

        if (data) {
          setActivity(dbToActivity(data));
        }
      } catch (err) {
        console.error('Failed to fetch activity:', err);
        setError(err instanceof Error ? err.message : 'Failed to load activity');
      } finally {
        setLoading(false);
      }
    };

    void fetchActivity();
  }, [id]);

  const handleRegister = () => {
    if (!user) {
      navigate('/login', { state: { returnTo: `/activities/${id}/register` } });
      return;
    }
    navigate(`/activities/${id}/register`);
  };

  const formatDate = (date: string): string => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (time: string | null): string => {
    if (!time) return 'TBD';
    return new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatCurrency = (cents: number): string => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const capitalizeWords = (str: string): string => {
    return str.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-b from-turquoise-800 via-turquoise-700 to-turquoise-800 min-h-screen">
        {/* Hero Gallery Skeleton */}
        <Skeleton className="w-full h-[400px] md:h-[500px] rounded-none" />

        <div className="container mx-auto px-4 py-8 -mt-20 relative z-10">
          {/* Back Button Skeleton */}
          <Skeleton className="h-10 w-40 mb-4" />

          {/* Main Content Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Details Skeleton */}
            <div className="lg:col-span-2 space-y-6">
              {/* Title Card Skeleton */}
              <Card className="backdrop-blur-md bg-turquoise-800/90">
                <CardHeader>
                  <Skeleton className="h-9 w-2/3 mb-3" />
                  <div className="flex flex-wrap gap-2">
                    <Skeleton className="h-7 w-24" />
                    <Skeleton className="h-7 w-24" />
                    <Skeleton className="h-7 w-20" />
                  </div>
                </CardHeader>
              </Card>

              {/* Description Skeleton */}
              <Card className="backdrop-blur-md bg-turquoise-800/90">
                <CardHeader>
                  <Skeleton className="h-6 w-48 mb-2" />
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                  </div>
                </CardContent>
              </Card>

              {/* Instructor Skeleton */}
              <Card className="backdrop-blur-md bg-turquoise-800/90">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-5 w-5" />
                    <Skeleton className="h-6 w-28" />
                  </div>
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-6 w-48" />
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Info Skeleton */}
            <div className="space-y-6">
              <Card className="backdrop-blur-md bg-turquoise-800/90">
                <CardHeader>
                  <Skeleton className="h-6 w-40" />
                </CardHeader>
                <CardContent className="space-y-4">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <Skeleton className="h-5 w-5 flex-shrink-0" />
                      <div className="flex-1">
                        <Skeleton className="h-4 w-20 mb-1" />
                        <Skeleton className="h-5 w-full" />
                      </div>
                    </div>
                  ))}
                  <div className="pt-4 border-t border-white/20">
                    <Skeleton className="h-11 w-full" />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !activity) {
    return (
      <div className="bg-gradient-to-b from-turquoise-800 via-turquoise-700 to-turquoise-800 min-h-screen">
        <div className="container mx-auto px-4 py-12">
          <div className="text-center">
            <div className="rounded-lg bg-red-900/40 border border-red-500/40 p-6 text-red-200 backdrop-blur-md shadow-lg inline-block">
              <p className="font-semibold">Error loading activity</p>
              <p className="mt-2 text-sm">{error || 'Activity not found'}</p>
              <Link to="/events">
                <Button className="mt-4" variant="secondary">
                  Back to Events
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const images = [activity.headerImageUrl, ...activity.galleryImages].filter(Boolean) as string[];

  return (
    <div className="bg-gradient-to-b from-turquoise-800 via-turquoise-700 to-turquoise-800 min-h-screen">
      {/* Hero Gallery */}
      {images.length > 0 && (
        <div className="relative w-full h-[400px] md:h-[500px] bg-turquoise-900">
          <img
            src={images[0]}
            alt={activity.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-turquoise-900/90 via-turquoise-900/50 to-transparent" />
        </div>
      )}

      <div className="container mx-auto px-4 py-8 -mt-20 relative z-10">
        {/* Back Button */}
        <Link to="/events">
          <Button variant="ghost" className="text-white hover:bg-white/10 mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Events
          </Button>
        </Link>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title Card */}
            <Card className="backdrop-blur-md bg-turquoise-800/90">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-3xl font-viking text-white mb-2">
                      {activity.title}
                    </CardTitle>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-3 py-1 bg-orange-500 text-white rounded-full text-sm font-medium">
                        {capitalizeWords(activity.type)}
                      </span>
                      {activity.skillLevel && (
                        <span className="px-3 py-1 bg-turquoise-600 text-white rounded-full text-sm font-medium">
                          {capitalizeWords(activity.skillLevel)}
                        </span>
                      )}
                      <span className="px-3 py-1 bg-white/20 text-white rounded-full text-sm font-medium">
                        {capitalizeWords(activity.status)}
                      </span>
                    </div>
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Description */}
            {activity.description && (
              <Card className="backdrop-blur-md bg-turquoise-800/90">
                <CardHeader>
                  <CardTitle className="text-white text-xl">About This Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-white/90 whitespace-pre-wrap">{activity.description}</p>
                </CardContent>
              </Card>
            )}

            {/* Instructor */}
            {activity.instructor && (
              <Card className="backdrop-blur-md bg-turquoise-800/90">
                <CardHeader>
                  <CardTitle className="text-white text-xl flex items-center gap-2">
                    <GraduationCap className="h-5 w-5 text-orange-400" />
                    Instructor
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-white/90 text-lg">{activity.instructor}</p>
                </CardContent>
              </Card>
            )}

            {/* Gallery */}
            {images.length > 1 && (
              <Card className="backdrop-blur-md bg-turquoise-800/90">
                <CardHeader>
                  <CardTitle className="text-white text-xl">Gallery</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {images.slice(1).map((img, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedImageIndex(index + 1)}
                        className="relative h-32 rounded-lg overflow-hidden hover:opacity-80 transition-opacity"
                      >
                        <img
                          src={img}
                          alt={`Gallery image ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Info & Registration */}
          <div className="space-y-6">
            {/* Quick Info */}
            <Card className="backdrop-blur-md bg-turquoise-800/90 sticky top-4">
              <CardHeader>
                <CardTitle className="text-white text-xl">Activity Info</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <Calendar className="h-5 w-5 text-orange-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-orange-300 font-medium">Date</p>
                    <p className="text-white">{formatDate(activity.date)}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-orange-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-orange-300 font-medium">Time & Duration</p>
                    <p className="text-white">
                      {formatTime(activity.startTime)}
                    </p>
                    <p className="text-white/80 text-sm">
                      Duration: {activity.duration} minutes
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-orange-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-orange-300 font-medium">Location</p>
                    <p className="text-white">Mesa Convention Center</p>
                  </div>
                </div>

                {activity.requiresRegistration && (
                  <>
                    <div className="flex items-start gap-3">
                      <Users className="h-5 w-5 text-orange-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-orange-300 font-medium">Participants</p>
                        <p className="text-white">
                          {activity.currentParticipants} / {activity.maxParticipants || 'Unlimited'}
                        </p>
                        {activity.maxParticipants && (
                          <div className="w-full bg-white/20 rounded-full h-2 mt-2">
                            <div
                              className="bg-orange-500 h-2 rounded-full transition-all"
                              style={{
                                width: `${(activity.currentParticipants / activity.maxParticipants) * 100}%`,
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <DollarSign className="h-5 w-5 text-orange-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-orange-300 font-medium">Fee</p>
                        <p className="text-white text-xl font-bold">
                          {formatCurrency(activity.fee)}
                        </p>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-white/20">
                      {REGISTRATIONS_CLOSED ? (
                        <ClosedNotice className="text-center" />
                      ) : (
                        <Button
                          onClick={handleRegister}
                          disabled={
                            activity.status === 'full' ||
                            activity.status === 'completed' ||
                            (activity.maxParticipants !== null && activity.currentParticipants >= activity.maxParticipants)
                          }
                          className="w-full"
                          size="lg"
                        >
                          <ActivityIcon className="h-5 w-5 mr-2" />
                          {activity.status === 'full' || (activity.maxParticipants !== null && activity.currentParticipants >= activity.maxParticipants)
                            ? 'Activity Full'
                            : activity.status === 'completed'
                            ? 'Activity Completed'
                            : 'Register Now'}
                        </Button>
                      )}
                    </div>
                  </>
                )}

                {!activity.requiresRegistration && (
                  <div className="pt-4 border-t border-white/20">
                    <div className="bg-white/10 rounded-lg p-4 text-center">
                      <p className="text-white font-medium">No Registration Required</p>
                      <p className="text-white/80 text-sm mt-1">Drop in anytime during the event</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Image Lightbox */}
      {selectedImageIndex !== null && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImageIndex(null)}
        >
          <button
            onClick={() => setSelectedImageIndex(null)}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="h-6 w-6 text-white" />
          </button>
          <img
            src={images[selectedImageIndex]}
            alt={`Gallery image ${selectedImageIndex}`}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};
